import { format } from 'date-fns';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFirestorePageQuery } from '@/hooks/useFirestorePageQuery';
import { useSettingsAccess } from '@/components/settings/useSettingsAccess';
import { PaginatedTable } from '@/components/ui/PaginatedTable';
import { SettingsSection } from '@/components/settings/SettingsSection';

type AuditRow = { id: string; action: string; at?: { seconds: number } };

function formatAuditTime(at?: { seconds: number }): string {
  if (!at?.seconds) return '—';
  return format(new Date(at.seconds * 1000), 'yyyy-MM-dd HH:mm');
}

export function AuditLogSection() {
  const { t } = useTranslation();
  const { orgId } = useSettingsAccess();

  const mapAudit = useCallback(
    (id: string, data: Record<string, unknown>): AuditRow => ({
      id,
      action: String(data.action ?? ''),
      at: data.at as { seconds: number } | undefined
    }),
    []
  );

  return (
    <SettingsSection id="settings-audit-log" lazy title={t('settings.auditLog')}>
      {(visible) => (visible ? <AuditLogInner orgId={orgId} mapAudit={mapAudit} t={t} /> : null)}
    </SettingsSection>
  );
}

function AuditLogInner({
  orgId,
  mapAudit,
  t
}: {
  orgId: string | null;
  mapAudit: (id: string, data: Record<string, unknown>) => AuditRow;
  t: (key: string) => string;
}) {
  const auditPage = useFirestorePageQuery<AuditRow>({
    orgId,
    collectionPath: 'auditLogs',
    orderByField: 'at',
    orderDirection: 'desc',
    mapDoc: mapAudit,
    enabled: Boolean(orgId)
  });

  return (
    <PaginatedTable
      loading={auditPage.loading}
      error={auditPage.error}
      pageIndex={auditPage.pageIndex}
      hasNext={auditPage.hasNext}
      hasPrev={auditPage.hasPrev}
      onNext={auditPage.nextPage}
      onPrev={auditPage.prevPage}
      pageSize={auditPage.pageSize}
      onPageSizeChange={auditPage.setPageSize}
      empty={
        !auditPage.rows.length ? (
          <p className="px-3 py-2 text-sm text-[var(--fv-text-muted)]">{t('settings.noAudit')}</p>
        ) : undefined
      }
    >
      <ul className="divide-y divide-[var(--fv-border)] text-sm text-[var(--fv-text-muted)]">
        {auditPage.rows.map((a) => (
          <li key={a.id} className="flex flex-wrap gap-2 px-3 py-2 transition-colors hover:bg-[var(--fv-bg)]">
            <span className="shrink-0 tabular-nums text-xs">{formatAuditTime(a.at)}</span>
            <span>{a.action}</span>
          </li>
        ))}
      </ul>
    </PaginatedTable>
  );
}
